import { useState, useEffect } from 'react'
import { Bar } from '@visx/shape'
import { localPoint } from '@visx/event'
import { useSpring, useSprings, animated } from 'react-spring'

import { useStyles } from './dynamic-marker.style'
import { lineChartService } from './line-chart.service'

const hoverPadding = 20

export const DynamicMarker = function ({
  series,
  scaleX,
  scaleY,
  width,
  height,
  marginLeft,
  selectedX,
  formatter,
  onMouseMove,
  onMouseLeave,
  onClick
}) {
  const classes = useStyles()

  const [hoverGroupProps, hoverGroupAnimation] = useSpring(() => ({ opacity: 0 }))
  const [hoverLineProps, hoverLineAnimation] = useSpring(() => ({}))
  const [hoverCircleProps, hoverCircleAnimation] = useSprings(series.length, () => ({}))
  const [xValueClosest, setXValueClosest] = useState()

  useEffect(() => {
    if (selectedX !== undefined) {
      showMarker(lineChartService.findClosestPoints(series, selectedX))
    } else {
      hideMarker()
    }

    function showMarker ({ closestPoints, closestCurrentPoint }) {
      if (!closestCurrentPoint) {
        return
      }

      const xValueClosest = closestCurrentPoint.x
      const xPixelClosest = scaleX(xValueClosest)

      let minPoint = { x: Infinity, y: Infinity }

      hoverLineAnimation.start({ x: xPixelClosest, x1: xPixelClosest, x2: xPixelClosest })
      hoverCircleAnimation.start((index) => {
        const point = closestPoints[index]
        let closestPoint = point.current ? point.current : (point.previous) ? point.previous : point.next
        if (closestPoint) {
          const cx = scaleX(closestPoint.x)
          const cy = scaleY(closestPoint.y)
          const isValidPoint = !isNaN(cx) && !isNaN(cy)
          if (Math.min(minPoint.y, cy) < minPoint.y) {
            minPoint = { x: cx, y: cy }
          }
          return {
            cx: isValidPoint ? cx : 0,
            cy: isValidPoint ? cy : 0,
            visibility: point.current && isValidPoint ? 'visible' : 'hidden'
          }
        } else {
          return {}
        }
      })
      hoverGroupAnimation.start({ opacity: 1 })
      setXValueClosest(xValueClosest)
    }

    function hideMarker () {
      hoverGroupAnimation.start({ opacity: 0 })
    }
  }, [ series, selectedX, scaleX, scaleY, hoverLineAnimation, hoverCircleAnimation, hoverGroupAnimation, setXValueClosest ])

  function handleMouseMove (event) {
    if (!event?.target?.ownerSVGElement || !onMouseMove) {
      return
    }

    onMouseMove(event, calculateEventLocations(event))
  }

  function handleClick (event) {
    if (!event?.target?.ownerSVGElement || !onClick) {
      return
    }

    onClick(event, calculateEventLocations(event))
  }

  function calculateEventLocations (event) {
    const { x: xPixelExact, y: yPixelExact } = localPoint(event)
    const xValueExact = scaleX.invert(xPixelExact - marginLeft)
    const { closestPoints, closestCurrentPoint } = lineChartService.findClosestPoints(series, xValueExact)
    const result = { xPixelExact, yPixelExact, closestPoints }
    if (closestCurrentPoint) {
      result.closestCurrentPoint = closestCurrentPoint
    }
    if (!isNaN(xValueExact)) {
      result.xValueExact = xValueExact
    }
    return result
  }

  return (
    <>
      <animated.g style={hoverGroupProps}>
        <animated.text
          y={-7}
          className={classes.hoverTopLabel}
          x={hoverLineProps.x}
        >
          {formatter ? formatter(xValueClosest) : xValueClosest.toString()}
        </animated.text>
        <animated.line
          y1={0}
          y2={height}
          stroke="gray"
          strokeWidth={1}
          strokeDasharray="5,4"
          x1={hoverLineProps.x1}
          x2={hoverLineProps.x2}
        />
        { series.map(({ id, color }, i) => {
          const springProps = hoverCircleProps[i]
          return (
            <animated.circle key={id}
              r={4}
              fill={color}
              stroke={color}
              strokeWidth={2}
              {...springProps}
            />
          )
        })}
      </animated.g>
      <Bar
        x={-hoverPadding}
        y={-hoverPadding}
        width={width + (hoverPadding * 2)}
        height={height + (hoverPadding * 2)}
        fill="transparent"
        rx={14}
        onMouseMove={handleMouseMove}
        onMouseLeave={onMouseLeave}
        onClick={handleClick}
      />
    </>
  )
}
